import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, CalendarBlank, ShieldCheck } from "@phosphor-icons/react/dist/ssr";
import { useTranslation } from "react-i18next";
import {
  trackEarlyAccessRequested,
  trackFormStart,
  trackFormSubmit,
  trackGenerateLead,
} from "@/analytics/ga4";
import { openCalendlyBooking } from "@/lib/calendly";
import { captureUTMParams, getStoredUTMParams, type UTMParams } from "@/lib/utm-manager";

type FieldName =
  | "workEmail"
  | "fullName"
  | "company"
  | "role"
  | "organizationType"
  | "countryJurisdiction"
  | "currentTradingSetup"
  | "primaryInterest"
  | "timeline"
  | "estimatedExposureOrVolume"
  | "message";

type CheckboxName =
  | "professionalUseConfirmed"
  | "nonCustodialAcknowledged"
  | "noAdviceNoDpmAcknowledged"
  | "contactConsent";

type FormState = Record<FieldName, string> & Record<CheckboxName, boolean>;
type FormErrors = Partial<Record<FieldName | CheckboxName | "form", string>>;
type Option = { value: string; label: string };

const FORM_ID = "early_access_request";
const SECTION_ID = "early_access";

const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "icloud.com",
  "proton.me",
  "protonmail.com",
  "gmx.com",
  "gmx.de",
  "web.de",
  "aol.com",
]);

const initialFormState: FormState = {
  workEmail: "",
  fullName: "",
  company: "",
  role: "",
  organizationType: "",
  countryJurisdiction: "",
  currentTradingSetup: "",
  primaryInterest: "",
  timeline: "",
  estimatedExposureOrVolume: "",
  message: "",
  professionalUseConfirmed: false,
  nonCustodialAcknowledged: false,
  noAdviceNoDpmAcknowledged: false,
  contactConsent: false,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getEmailDomain = (email: string) => email.trim().toLowerCase().split("@").pop() || "";

const isProfessionalEmail = (email: string) => {
  const domain = getEmailDomain(email);
  return Boolean(domain) && !PERSONAL_EMAIL_DOMAINS.has(domain);
};

const fieldClass = (hasError: boolean) => [
  "w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-gray-900 shadow-sm outline-none transition",
  "placeholder:text-gray-400 focus:border-red-500 focus:ring-2 focus:ring-red-100",
  hasError ? "border-red-400" : "border-gray-200",
].join(" ");

const labelClass = "mb-1.5 block text-sm font-semibold text-gray-900";
const errorClass = "mt-1.5 text-xs font-medium text-red-600";

export function EarlyAccessForm() {
  const { t, i18n } = useTranslation("common");
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [leadId, setLeadId] = useState<string | number | null>(null);
  const [utmParams, setUtmParams] = useState<UTMParams>({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
  });
  const formStartedRef = useRef(false);

  useEffect(() => {
    setUtmParams(captureUTMParams());
  }, []);

  const language = useMemo(
    () => (i18n.resolvedLanguage || i18n.language || "en").split("-")[0],
    [i18n.language, i18n.resolvedLanguage],
  );

  const options = (key: string) =>
    t(`earlyAccess.form.options.${key}`, { returnObjects: true }) as unknown as Option[];

  const fieldLabel = (field: FieldName) => t(`earlyAccess.form.fields.${field}.label`);
  const fieldPlaceholder = (field: FieldName) => t(`earlyAccess.form.fields.${field}.placeholder`);

  const markStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackFormStart({ form_id: FORM_ID, section_id: SECTION_ID });
  };

  const updateField = (field: FieldName, value: string) => {
    markStarted();
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: undefined, form: undefined }));
    }
  };

  const updateCheckbox = (field: CheckboxName, value: boolean) => {
    markStarted();
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: undefined, form: undefined }));
    }
  };

  const validate = () => {
    const nextErrors: FormErrors = {};

    if (!form.workEmail.trim()) {
      nextErrors.workEmail = t("earlyAccess.form.validation.required");
    } else if (!emailPattern.test(form.workEmail.trim())) {
      nextErrors.workEmail = t("earlyAccess.form.validation.email");
    } else if (!isProfessionalEmail(form.workEmail)) {
      nextErrors.workEmail = t("earlyAccess.form.validation.professionalEmail");
    }

    const requiredFields: FieldName[] = [
      "fullName",
      "company",
      "role",
      "organizationType",
      "countryJurisdiction",
      "currentTradingSetup",
      "primaryInterest",
      "timeline",
    ];

    requiredFields.forEach((field) => {
      if (!form[field].trim()) {
        nextErrors[field] = t("earlyAccess.form.validation.required");
      }
    });

    const requiredChecks: CheckboxName[] = [
      "professionalUseConfirmed",
      "nonCustodialAcknowledged",
      "noAdviceNoDpmAcknowledged",
      "contactConsent",
    ];

    requiredChecks.forEach((field) => {
      if (!form[field]) {
        nextErrors[field] = t("earlyAccess.form.validation.checkbox");
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    const storedUtm = getStoredUTMParams();
    const submittedAt = new Date().toISOString();

    return {
      leadType: "early_access",
      workEmail: form.workEmail.trim(),
      fullName: form.fullName.trim(),
      company: form.company.trim(),
      role: form.role,
      organizationType: form.organizationType,
      countryJurisdiction: form.countryJurisdiction,
      currentTradingSetup: form.currentTradingSetup,
      primaryInterest: form.primaryInterest,
      timeline: form.timeline,
      estimatedExposureOrVolume: form.estimatedExposureOrVolume,
      message: form.message.trim(),
      professionalUseConfirmed: form.professionalUseConfirmed,
      nonCustodialAcknowledged: form.nonCustodialAcknowledged,
      noAdviceNoDpmAcknowledged: form.noAdviceNoDpmAcknowledged,
      nonCustodialNoAdviceAcknowledged: form.nonCustodialAcknowledged && form.noAdviceNoDpmAcknowledged,
      contactConsent: form.contactConsent,
      language,
      submittedAt,
      pagePath: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "",
      utm_source: utmParams.utm_source || storedUtm.utm_source || "",
      utm_medium: utmParams.utm_medium || storedUtm.utm_medium || "",
      utm_campaign: utmParams.utm_campaign || storedUtm.utm_campaign || "",
      utm_term: utmParams.utm_term || storedUtm.utm_term || "",
      utm_content: utmParams.utm_content || storedUtm.utm_content || "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
      email: form.workEmail.trim(),
      name: form.fullName.trim(),
      interest: form.primaryInterest,
      selectedPlan: "Early access",
      consent: form.contactConsent,
      ts: submittedAt,
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    markStarted();

    if (!validate()) {
      trackFormSubmit({ form_id: FORM_ID, section_id: SECTION_ID, outcome: "error" });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    const payload = buildPayload();

    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.error || t("earlyAccess.form.validation.submitError"));
      }

      setLeadId(result.lead_id || null);
      setIsSubmitted(true);
      trackFormSubmit({ form_id: FORM_ID, section_id: SECTION_ID, outcome: "success" });
      trackGenerateLead({ lead_type: "early_access", form_id: FORM_ID, section_id: SECTION_ID });
      trackEarlyAccessRequested({
        organizationType: form.organizationType,
        role: form.role,
        countryJurisdiction: form.countryJurisdiction,
        currentTradingSetup: form.currentTradingSetup,
        primaryInterest: form.primaryInterest,
        timeline: form.timeline,
        hasProfessionalEmail: isProfessionalEmail(form.workEmail),
        language,
      });
    } catch (error) {
      setErrors({
        form: error instanceof Error ? error.message : t("earlyAccess.form.validation.submitError"),
      });
      trackFormSubmit({ form_id: FORM_ID, section_id: SECTION_ID, outcome: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSelect = (field: FieldName, optionKey: string) => {
    const errorId = `early-access-${field}-error`;

    return (
      <div>
        <label htmlFor={`early-access-${field}`} className={labelClass}>
          {fieldLabel(field)}
        </label>
        <select
          id={`early-access-${field}`}
          value={form[field]}
          onChange={(event) => updateField(field, event.target.value)}
          className={fieldClass(Boolean(errors[field]))}
          aria-invalid={Boolean(errors[field])}
          aria-describedby={errors[field] ? errorId : undefined}
        >
          <option value="">{t("earlyAccess.form.selectPlaceholder")}</option>
          {options(optionKey).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors[field] ? <p id={errorId} className={errorClass}>{errors[field]}</p> : null}
      </div>
    );
  };

  const renderInput = (field: FieldName, type = "text") => {
    const errorId = `early-access-${field}-error`;

    return (
      <div>
        <label htmlFor={`early-access-${field}`} className={labelClass}>
          {fieldLabel(field)}
        </label>
        <input
          id={`early-access-${field}`}
          type={type}
          value={form[field]}
          onChange={(event) => updateField(field, event.target.value)}
          placeholder={fieldPlaceholder(field)}
          className={fieldClass(Boolean(errors[field]))}
          aria-invalid={Boolean(errors[field])}
          aria-describedby={errors[field] ? errorId : undefined}
          autoComplete={field === "workEmail" ? "email" : field === "fullName" ? "name" : "organization"}
        />
        {errors[field] ? <p id={errorId} className={errorClass}>{errors[field]}</p> : null}
      </div>
    );
  };

  const checkboxItems: Array<{ field: CheckboxName; label: string }> = [
    { field: "professionalUseConfirmed", label: t("earlyAccess.form.checkboxes.professionalUse") },
    { field: "nonCustodialAcknowledged", label: t("earlyAccess.form.checkboxes.nonCustodial") },
    { field: "noAdviceNoDpmAcknowledged", label: t("earlyAccess.form.checkboxes.noAdviceNoDpm") },
    { field: "contactConsent", label: t("earlyAccess.form.checkboxes.contactConsent") },
  ];

  if (isSubmitted) {
    return (
      <div className="mx-auto max-w-4xl rounded-[1.5rem] border border-gray-200 bg-white p-8 shadow-xl md:p-12">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
            <CheckCircle size={30} weight="fill" />
          </div>
          <h2 className="mb-4 text-3xl font-bold text-gray-950">{t("earlyAccess.success.title")}</h2>
          <p className="text-base leading-relaxed text-gray-600">{t("earlyAccess.success.body")}</p>
          {leadId ? (
            <p className="mt-4 text-xs font-medium uppercase tracking-[0.2em] text-gray-400">
              {t("earlyAccess.success.reference")} {leadId}
            </p>
          ) : null}
          <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              {t("earlyAccess.success.calendlyPrompt")}
            </p>
            <button
              type="button"
              onClick={() => openCalendlyBooking("early_access_success")}
              className="btn-outline-quiet inline-flex items-center justify-center px-5 py-3 text-sm font-semibold"
              data-cta-id="early_access_preview_call"
              data-cta-section="early_access_success"
              data-cta-label={t("earlyAccess.success.calendlyCta")}
            >
              <CalendarBlank size={16} weight="regular" className="mr-2" />
              {t("earlyAccess.success.calendlyCta")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
      <div className="rounded-[1.5rem] p-8 text-white shadow-xl md:p-10" style={{ backgroundColor: "var(--gray-900)" }}>
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-red-300">
          {t("earlyAccess.eyebrow")}
        </p>
        <h2 className="mb-5 text-3xl font-bold leading-tight text-white md:text-4xl">
          {t("earlyAccess.form.title")}
        </h2>
        <p className="mb-8 text-sm leading-relaxed text-gray-300">
          {t("earlyAccess.form.subtitle")}
        </p>
        <div className="space-y-4">
          {(t("earlyAccess.form.qualifiers", { returnObjects: true }) as unknown as string[]).map((item) => (
            <div key={item} className="flex items-start gap-3 text-sm text-gray-300">
              <ShieldCheck size={18} weight="fill" className="mt-0.5 flex-shrink-0 text-red-300" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 border-t border-white/10 pt-6 text-xs leading-relaxed text-gray-400">
          {t("earlyAccess.form.compliance")}
        </p>
      </div>

      <form
        id="early-access-form"
        noValidate
        onSubmit={handleSubmit}
        className="rounded-[1.5rem] border border-gray-200 bg-white p-6 shadow-xl md:p-8"
      >
        <div className="space-y-8">
          <fieldset className="space-y-4">
            <legend className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
              {t("earlyAccess.form.groups.details")}
            </legend>
            <div className="grid gap-4 md:grid-cols-2">
              {renderInput("workEmail", "email")}
              {renderInput("fullName")}
              {renderInput("company")}
              {renderSelect("role", "role")}
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
              {t("earlyAccess.form.groups.setup")}
            </legend>
            <div className="grid gap-4 md:grid-cols-2">
              {renderSelect("organizationType", "organizationType")}
              {renderSelect("countryJurisdiction", "countryJurisdiction")}
              {renderSelect("currentTradingSetup", "currentTradingSetup")}
              {renderSelect("estimatedExposureOrVolume", "estimatedExposureOrVolume")}
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
              {t("earlyAccess.form.groups.access")}
            </legend>
            <div className="grid gap-4 md:grid-cols-2">
              {renderSelect("primaryInterest", "primaryInterest")}
              {renderSelect("timeline", "timeline")}
            </div>
            <div>
              <label htmlFor="early-access-message" className={labelClass}>
                {fieldLabel("message")}
              </label>
              <textarea
                id="early-access-message"
                value={form.message}
                onChange={(event) => updateField("message", event.target.value)}
                placeholder={fieldPlaceholder("message")}
                rows={4}
                className={`${fieldClass(Boolean(errors.message))} min-h-[112px] resize-y`}
                aria-invalid={Boolean(errors.message)}
              />
            </div>
          </fieldset>

          <div className="space-y-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
            {checkboxItems.map((item) => {
              const errorId = `early-access-${item.field}-error`;
              return (
                <div key={item.field}>
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-gray-700">
                    <input
                      type="checkbox"
                      checked={form[item.field]}
                      onChange={(event) => updateCheckbox(item.field, event.target.checked)}
                      className="mt-1 h-4 w-4 flex-none shrink-0 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      aria-invalid={Boolean(errors[item.field])}
                      aria-describedby={errors[item.field] ? errorId : undefined}
                    />
                    <span>{item.label}</span>
                  </label>
                  {errors[item.field] ? <p id={errorId} className={`${errorClass} pl-7`}>{errors[item.field]}</p> : null}
                </div>
              );
            })}
          </div>

          {errors.form ? (
            <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {errors.form}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary inline-flex w-full items-center justify-center rounded-xl px-7 py-3.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            data-cta-id="early_access_submit"
            data-cta-section="early_access"
            data-cta-label={t("earlyAccess.cta.primary")}
          >
            {isSubmitting ? t("earlyAccess.form.submitting") : t("earlyAccess.cta.primary")}
          </button>
        </div>
      </form>
    </div>
  );
}
